import { Component, createRef } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import I18n from 'I18n';
import { PDFViewerManager } from '../../lib/PDFViewerManager';
import PDFKeyControls from './PDFKeyControls';
import PDFControls from './PDFControls';
import PDFLoading from './PDFLoading';
import PDFError from './PDFError';
import { connect } from 'react-redux';
import * as PDFActions from '../../actions/PDFActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class PDFViewer extends Component {
  constructor(...args) {
    super(...args);
    this.containerRef = /*#__PURE__*/createRef();
    this.manager = new PDFViewerManager();
    this.scrollCooldown = false;
    this.state = {
      hovered: false,
      progress: null,
      pdf: null,
      error: null
    };
    this.loadPDF = async () => {
      const {
        file,
        pageIndex,
        zoom,
        onPage,
        onChangePageCHIRP
      } = this.props;
      try {
        await this.manager.load(file, {
          onPageChange: ({
            pageIndex: newPageIndex,
            leavingPageNumber,
            enteringPageNumber
          }) => {
            onChangePageCHIRP({
              leavingPageNumber,
              enteringPageNumber
            });
            onPage(newPageIndex);
          },
          onProgress: progress => this.setState({
            progress
          }),
          onReady: pdf => this.setState({
            pdf,
            progress: null
          }),
          onError: error => this.setState({
            error
          })
        }, {
          pageIndex,
          zoom
        });
      } catch (error) {
        this.setState({
          error: error
        });
      }
    };
    this.retry = () => {
      window.location.reload();
    };
    this.handleMouseEnter = () => {
      this.setState({
        hovered: true
      });
    };
    this.handleMouseLeave = () => {
      this.setState({
        hovered: false
      });
    };
    this.handleWheel = event => {
      if (this.scrollCooldown) return;
      const {
        pageIndex,
        onPage
      } = this.props;
      const {
        pdf
      } = this.state;
      if (!pdf) return;
      const target = event.currentTarget;
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = target;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const totalPages = pdf.numPages;
      if (event.deltaY > 0 && isAtBottom && pageIndex < totalPages - 1) {
        onPage(pageIndex + 1);
        target.scrollTop = 0;
        this.scrollCooldown = true;
        setTimeout(() => {
          this.scrollCooldown = false;
        }, 400);
      } else if (event.deltaY < 0 && isAtTop && pageIndex > 0) {
        onPage(pageIndex - 1);
        setTimeout(() => {
          target.scrollTop = target.scrollHeight - target.clientHeight;
        }, 0);
        this.scrollCooldown = true;
        setTimeout(() => {
          this.scrollCooldown = false;
        }, 400);
      }
    };
    this.renderProgress = () => {
      const {
        pageIndex,
        imagePlaceholders
      } = this.props;
      const {
        progress
      } = this.state;
      return /*#__PURE__*/_jsx(PDFLoading, {
        pageIndex: pageIndex,
        imagePlaceholders: imagePlaceholders,
        value: progress !== null && progress !== void 0 ? progress : 0
      });
    };
    this.renderPDFKeyControls = () => {
      const {
        pageIndex,
        onPage,
        onChangePageCHIRP
      } = this.props;
      const {
        pdf
      } = this.state;
      if (!pdf) {
        return null;
      }
      return /*#__PURE__*/_jsx(PDFKeyControls, {
        onPage: onPage,
        onChangePageCHIRP: onChangePageCHIRP,
        pageIndex: pageIndex,
        pageNumber: pageIndex + 1,
        totalPages: pdf.numPages,
        children: /*#__PURE__*/_jsx("div", {
          "data-test-id": "pdf-viewer",
          onWheel: this.handleWheel,
          style: {
            display: 'flex',
            flex: 1,
            padding: 'var(--spacing-500)',
            overflow: 'auto',
            position: 'relative',
            justifyContent: 'center'
          },
          children: /*#__PURE__*/_jsx("div", {
            ref: this.containerRef,
            style: {
              position: 'absolute',
              top: 20,
              bottom: 20,
              left: 20,
              right: 20,
              display: 'flex',
              justifyContent: 'center'
            },
            children: /*#__PURE__*/_jsx("div", {
              id: "viewer",
              className: "pdfViewer"
            })
          })
        })
      });
    };
    this.renderPDFControls = () => {
      const {
        branding,
        pageIndex,
        zoom,
        onPage,
        onZoom,
        onChangePageCHIRP
      } = this.props;
      const {
        pdf,
        hovered
      } = this.state;
      if (!pdf) {
        return null;
      }
      return /*#__PURE__*/_jsx(Flex, {
        justifyContent: "center",
        position: "absolute",
        insetBlockEnd: branding.showSalesAd ? '1500' : '1000',
        insetInlineStart: "0",
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(PDFControls, {
          visible: hovered,
          pageIndex: pageIndex,
          pageNumber: pageIndex + 1,
          totalPages: pdf.numPages,
          zoom: zoom,
          onPage: onPage,
          onChangePageCHIRP: onChangePageCHIRP,
          onZoom: onZoom
        })
      });
    };
  }
  componentDidMount() {
    this.props.onReset();
    void this.loadPDF();
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      pageIndex,
      zoom
    } = this.props;
    const {
      pdf
    } = this.state;
    if (prevProps.pageIndex !== pageIndex) {
      this.manager.setPage(pageIndex);
    }
    if (prevProps.zoom !== zoom) {
      this.manager.setZoom(zoom);
    }
    if (!prevState.pdf && pdf && this.containerRef.current) {
      this.manager.setContainer(this.containerRef.current);
    }
  }
  componentWillUnmount() {
    this.manager.destroy();
  }
  render() {
    const {
      error,
      pdf
    } = this.state;
    const {
      pageIndex,
      presentationName
    } = this.props;
    if (error !== null) {
      return /*#__PURE__*/_jsx(PDFError, {
        onRetry: this.retry,
        isPDFError: false
      });
    }
    if (pdf === null) {
      return this.renderProgress();
    }
    return /*#__PURE__*/_jsxs(Flex, {
      "aria-label": I18n.text('a11y.viewSpecificPage', {
        pageNumber: pageIndex + 1,
        documentName: presentationName
      }),
      tabIndex: 0,
      flex: 1,
      position: "relative",
      overflow: "hidden",
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      children: [this.renderPDFKeyControls(), this.renderPDFControls()]
    });
  }
}
PDFViewer.defaultProps = {
  imagePlaceholders: []
};
export default connect(({
  view: {
    pdf
  }
}) => pdf, {
  onPage: PDFActions.changePage,
  onChangePageCHIRP: PDFActions.changePageCHIRP,
  onZoom: PDFActions.changeZoom,
  onReset: PDFActions.reset
})(PDFViewer);